// services/exifService.js
// EXIF metadata extraction from uploaded photos using exifr
// Extracts GPS coordinates, altitude, bearing, accuracy, and capture datetime

import exifr from "exifr";

/**
 * Extract geo-metadata from a photo file's EXIF data.
 * Handles JPEG/TIFF with GPS tags. Returns null if no GPS data found.
 *
 * @param {File} file - The image File object from file input
 * @returns {Promise<object|null>} Geo metadata object or null if no GPS data
 *   { latitude, longitude, altitude, bearing, accuracy, capturedAt }
 */
export const extractGeoFromExif = async (file) => {
  try {
    const exif = await exifr.parse(file, {
      gps: true,
      pick: [
        "GPSLatitude",
        "GPSLatitudeRef",
        "GPSLongitude",
        "GPSLongitudeRef",
        "GPSAltitude",
        "GPSAltitudeRef",
        "GPSImgDirection",
        "GPSHPositioningError",
        "DateTimeOriginal",
        "CreateDate",
      ],
    });

    if (!exif) return null;

    // exifr auto-converts GPS DMS to decimal degrees
    const latitude = exif.latitude ?? null;
    const longitude = exif.longitude ?? null;

    // No GPS coordinates means no useful geo data
    if (latitude == null || longitude == null) return null;

    // DateTimeOriginal is parsed by exifr as a Date in the browser's local timezone
    const captureDate = exif.DateTimeOriginal || exif.CreateDate || null;
    const localFmt = {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    return {
      latitude,
      longitude,
      altitude: exif.GPSAltitude ?? null,
      bearing: exif.GPSImgDirection ?? null,
      accuracy: exif.GPSHPositioningError ?? null,
      capturedAt: captureDate ? captureDate.toISOString() : null,
      capturedAtLocal: captureDate
        ? captureDate.toLocaleString("en-US", localFmt)
        : null,
    };
  } catch (e) {
    console.warn("EXIF extraction failed:", e);
    return null;
  }
};
